import Router from 'bpm-router';

import { bindEvents, getRootOffsetBounds, getOffsetBounds, clamp } from '../../../assets/js/helpers';

import App from '../../../assets/js/app';

import AnimationController from '../../../assets/js/controller/animation';
import ResizeController from '../../../assets/js/controller/resize';
import ScrollController from '../../../assets/js/controller/scroll';

const selector = 'hero-bottom';

export default class HeroBottom {

	constructor(section) {
		this.el = {
			section,
			cta: section.querySelector(`.${selector}__cta`),
			progress: section.querySelector(`.${selector}__progress`),
			hero: section.querySelector('.hero-experience-detail') || section.querySelector('.hero-interview'),
			mainNav: document.querySelector('#navigation-primary')
		};

		// class variable groups

		this.bounds = {
			winHeight: 0,
			top: 0,
			scrollMax: 0,
			scrollProgress: 0,
			heroOffsetTop: 0,
			scale: 0
		};

		this.config = {
			minLoadTime: 400
		};

		this.controllers = {
			animation: null,
			resize: null
		};

		this.coords = {
			position: 0,
			scaleY: 0
		};

		this.flags = {
			isNavigating: false
		};

		this.state = {
			scrollProgress: 0,
			loadTime: {
				start: 0,
				minComplete: 0
			}
		};

		// class variables

		this.scrollLock = null;

		// build url for next page

		this.url = this.el.section.getAttribute('data-url');
		this.urlParts = this.url.split('/');
		this.urlParts.shift();

		// bind events to class scope

		this.events = bindEvents(this);

		setTimeout(() => {
			this.init();
		}, 100);
	}

	static get selector() {
		return selector;
	}

	init() {
		this.detachFromPage();
		this.measure();
		this.addEventListeners();

		// preload page data

		App.load(this.urlParts[0], this.urlParts[1]);
	}

	// event listeners

	addEventListeners() {
		this.controllers.animation = AnimationController.set({
			update: this.update.bind(this),
			render: this.render.bind(this)
		});

		this.controllers.resize = ResizeController.set({
			update: () => { this.measure(); },
			resize: () => {}
		});
	}

	removeEventListeners() {
		AnimationController.clear(this.controllers.animation);
		ResizeController.clear(this.controllers.resize);
	}

	// measurement calculations

	measure() {
		const bounds = getRootOffsetBounds(this.el.section);
		const heroBounds = getOffsetBounds(this.el.hero);

		this.bounds.winHeight = window.innerHeight;
		this.bounds.top = bounds.top;
		this.bounds.scrollMax = bounds.height - this.bounds.winHeight;
		this.bounds.heroOffsetTop = heroBounds.top;
		this.bounds.scale = this.bounds.heroOffsetTop / this.bounds.scrollMax;

		this.scrollLock = this.bounds.top + this.bounds.scrollMax;

		this.el.hero.style.paddingTop = `${this.el.mainNav.clientHeight + 30}px`;
	}

	// determine whether to load next page based on scroll position

	update() {
		const { position } = ScrollController;

		if (position >= this.bounds.top) {
			this.coords.y = position - this.bounds.top;
			this.coords.scaleY = clamp(this.coords.y, 0, this.bounds.scrollMax);
			this.state.scrollProgress = clamp(this.coords.scaleY / this.bounds.scrollMax, 0, 1);

			if (this.coords.scaleY >= (this.bounds.scrollMax - 500)) {
				ScrollController.lockScroll();
				ScrollController.scrollTo(this.scrollLock);
			}

			if (this.coords.scaleY === this.bounds.scrollMax) {
				if (!this.flags.isNavigating) {
					this.flags.isNavigating = true;
					this.animateOut();
				}
			}

		} else {
			this.coords.y = 0;
			this.coords.scaleY = 0;
			this.state.scrollProgress = 0;
		}

		return position !== this.coords.y;
	}

	render() {
		this.el.section.style.transform = `translate3d(0, ${this.coords.scaleY}px, 0)`;
		this.el.hero.style.transform = `translate3d(0, ${0 - (this.bounds.heroOffsetTop * this.state.scrollProgress)}px, 0)`;
		this.el.progress.style.transform = `scaleX(${this.state.scrollProgress})`;

		this.el.cta.style.opacity = 1 - (this.state.scrollProgress * 1.15);
		this.el.cta.style.transform = `translate3d(0, ${(0 - (this.bounds.heroOffsetTop * this.state.scrollProgress) * 0.5)}px, 0)`;
	}

	// transition out for page change

	animateOut() {
		this.el.mainNav.classList.add('is-navigating');

		// make sure scrolling doesn't occur too quickly
		this.state.loadTime.start = Date.now();
		this.state.loadTime.minComplete = Date.now() + this.config.minLoadTime;

		// open next page
		App.open(this.urlParts[0], this.urlParts[1], false);
		Router.navigate(this.url, true);
	}

	// move outside of page container

	detachFromPage() {
		const $page = this.el.section.closest('.page');
		const $pageParent = $page.parentElement;
		$pageParent.appendChild(this.el.section);

		this.el.section.classList.add('is-detached-module');
	}

	// destroy

	destroy() {
		this.removeEventListeners();
		this.el.section.classList.remove('is-navigating');
		this.el.mainNav.classList.remove('is-navigating');

		this.el.hero.style.paddingTop = '';

		this.el.section.style.transform = 'translate3d(0, 0, 0)';
		this.el.hero.style.transform = 'translate3d(0, 0, 0)';
		this.el.cta.style.transform = 'translate3d(0, 0, 0)';

		// lock scrolling if page loaded too quickly

		const scrollStartDelay = Math.max(0, this.state.loadTime.minComplete - Date.now());
		const $html = document.documentElement;
		$html.classList.add('is-navigation-scroll-locked'); // TODO: dirty, need more systematic way

		setTimeout(() => {
			$html.classList.remove('is-navigation-scroll-locked');
		}, scrollStartDelay);
	}

}
